<template>
  <!-- :key wymusza remount przy zmianie ścieżki (dodatkowy bezpiecznik) -->
  <div class="product-detail-bg" :key="$route.fullPath">
    <div class="product-detail-box">
      <Toast v-if="showToast" :title="translations.product.toast_title" :message="toastMessage" />

      <div class="product-detail-cols">
        <!-- LEWA KOLUMNA (zdjęcia) -->
        <div class="product-col-left">
          <div class="main-image-wrap">
            <button class="arrow left" @click="prevImage">&#10094;</button>
            <img
                :src="mainImage || product.get_image || product.get_thumbnail"
                :alt="product.name"
                class="main-image"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="640"
                height="640"
                @touchstart="handleTouchStart"
                @touchend="handleTouchEnd"
              />

            <button class="arrow right" @click="nextImage">&#10095;</button>
          </div>

          <div class="thumbs-row" v-if="thumbnails.length > 1">

          <img
          v-for="(img, idx) in thumbnails"
          :key="img + idx"
          :src="img"
          :alt="(translations.product.thumbnail_alt || 'Miniatura') + ' ' + (idx + 1)"
          class="thumb"
          :class="{ active: img === mainImage }"
          loading="lazy"
          decoding="async"
          width="96"
          height="96"
          @click="changeMainImage(img)"
        />

          </div>
        </div>

        <!-- PRAWA KOLUMNA -->
        <div class="product-col-right">
          <h1 class="prod-title">{{ product.name }}</h1>

          <div class="prod-price">
            <span v-if="selectedVariant">
              {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
            </span>
            <span v-else class="prod-notice">
              {{ translations.product.select_variant_notice }}
            </span>
          </div>

          <div class="picker-heading">
            {{ translations.product.select_variant_notice || 'Wybierz wariant' }}
          </div>

          <div class="variant-picker" @click="isDrawerOpen = true" role="button" tabindex="0">
            <span class="picker-value">
              {{
                selectedVariant?.type?.name
                  || (translations.product.select_variant_notice || 'Wybierz wariant')
              }}
            </span>

            <span class="picker-right">
              <span class="picker-price" v-if="selectedVariant">
                {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
              </span>
              <svg class="picker-caret" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>

          <button class="big-cart-btn" :disabled="!selectedVariant" @click="addToCart">
            {{ translations.product.add_to_cart }}
          </button>

          <div v-if="product.production_time" class="prod-prodtime">
            {{ translations.product.production_time }}:
            <strong>{{ product.production_time }} {{ translations.product.days }}</strong>
          </div>
          <div v-if="selectedDeliveryTime" class="prod-deliverytime">
            {{ translations.product.delivery_time }}:
            <strong>{{ selectedDeliveryTime }} {{ translations.product.days }}</strong>
          </div>
          <div v-if="product.production_time && selectedDeliveryTime" class="prod-totaltime">
            {{ translations.product.total_delivery_time }}:
            <strong>{{ totalDeliveryTime }} {{ translations.product.days }}</strong>
          </div>

          <div v-if="availableCountries.length" class="shipping-box">
            <label for="country">{{ translations.product.select_country }}</label>
            <select
              v-model="selectedCountry"
              id="country"
              class="styled-select"
              @change="saveUserCountryISO(selectedCountry); updateShippingPrice()"
            >
              <option v-for="country in availableCountries" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </select>
            <div class="shipping-price">
              {{ translations.product.shipping_cost }}
              <strong>{{ selectedShippingPrice.toFixed(2) }} {{ getCurrencySymbol() }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div 
          v-if="product.description && descVisible"
          class="product-description"
          v-html="product.description">
      </div> 

      <!-- POLECANE PRODUKTY – KARUZELA -->
      <div v-if="otherProducts.length" class="rec-wrap">
        <div class="rec-head">
          <h2 class="rec-title">{{ translations.product?.recommended || '' }}</h2>
          <div class="rec-controls">
            <button class="rec-btn" @click="scrollRec(-1)" aria-label="Poprzednie">‹</button>
            <button class="rec-btn" @click="scrollRec(1)" aria-label="Następne">›</button>
          </div>
        </div>

        <div ref="recRow" class="rec-row" role="region" :aria-label="translations.product?.recommended || 'Polecane produkty'">
          <AppProductCard
            v-for="p in otherProducts"
            :key="p.id"
            :id="p.id"
            :slug="p.slug"
            :img="p.get_image"
            :name="p.name"
            :price="getListPriceForCurrency(p)"
            :currency="getCurrencySymbol()"
            class="rec-item"
          />
        </div>
      </div>
    </div>

    <!-- STICKY KOSZYK -->
    <div v-if="showStickyCart" class="sticky-cart">
      <div class="sticky-cart-content">
        <template v-if="isMobile">
          <span class="sticky-price">
            {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
          </span>
        </template>
        <template v-else>
          <span class="sticky-info">
            {{ product.name }}
            <span v-if="selectedVariant">– {{ selectedVariant.type?.name }}</span>
          </span>
          <span class="sticky-price">
            {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
          </span>
        </template>
      </div>
      <button class="sticky-add-btn" :disabled="!selectedVariant" @click="addToCart">
        {{ translations.product.add_to_cart }}
      </button>
    </div>

    <!-- BOTTOM-SHEET -->
    <div v-if="isDrawerOpen" class="variant-backdrop" @click="closeDrawer" />
    <aside class="variant-sheet" :class="{ open: isDrawerOpen }" role="dialog" aria-modal="true">
      <header class="variant-sheet-header">
        <strong>{{ translations.product.select_variant_notice || 'Wybierz wariant' }}</strong>
        <button class="sheet-close" @click="closeDrawer" aria-label="Zamknij">×</button>
      </header>

      <div class="variant-sheet-body">
        <div class="sizes-column">
          <button
            v-for="variant in allVariants"
            :key="variant.id"
            type="button"
            class="size-row"
            :class="{ active: selectedVariantId === String(variant.id) }"
            @click="selectVariant(variant); closeDrawer()"
          >
            <span class="size-name">{{ variant.type?.name }}</span>
            <span class="size-price" v-if="getPriceForCurrency(variant)">
              {{ getPriceForCurrency(variant) }} {{ getCurrencySymbol() }}
            </span>
          </button>
        </div>
      </div>

      <footer class="variant-sheet-footer">
        <button class="big-cart-btn" :disabled="!selectedVariant" @click="addToCart(); closeDrawer()">
          {{ translations.product.add_to_cart }}
        </button>
      </footer>
    </aside>
  </div>
</template>

<script>
import Toast from './AppToast.vue'
import AppProductCard from './AppProductCard.vue'
import eventBus from "@/eventBus.js"
import { translations } from '@/locales/lang'
import { currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'
import { track } from '@/tracking/events'

export default {
  name: "AppProductDetail",
  components: { Toast, AppProductCard },
  data() {
    return {
      product: {},
      loading: true,
      error: null,
      thumbnails: [],
      mainImage: null,

      variantGroups: {},
      selectedVariant: null,
      selectedVariantId: "",

      selectedCountry: '',
      availableCountries: [],
      selectedShippingPrice: 0,
      selectedDeliveryTime: 0,

      touchStartX: 0,
      showToast: false,
      toastMessage: '',
      isMobile: false,
      showStickyCart: false,

      isDrawerOpen: false,

      otherProducts: [],
      descVisible: false, // dopisano 13.02.2026
    otherLoaded: false, // dopisano 13.02.2026
    };
  },
  computed: {
    translations() { return translations.value; },
    currentCurrency() { return currentCurrency.value; },
    activeLang() { return currentLang.value; },
    totalDeliveryTime() {
      return (Number(this.product.production_time || 0) + Number(this.selectedDeliveryTime || 0));
    },
    allVariants() {
      return Object.values(this.variantGroups || {}).flat();
    },
  },
  watch: {
    currentCurrency() {
      if (this.selectedVariant) this.selectVariant(this.selectedVariant);
    },
    selectedVariantId(newId) {
      const v = this.allVariants.find(x => String(x.id) === String(newId));
      if (v) this.selectVariant(v);
    },
    // język → przeładuj
    async activeLang() {
      const prevVariantId = this.selectedVariant?.id;
      await this.fetchProduct();
      if (prevVariantId) {
        const same = this.allVariants.find(v => String(v.id) === String(prevVariantId));
        if (same) this.selectVariant(same);
      }
      await this.fetchOtherProducts();
    },
    // ZMIANA PRODUKTU (np. z karuzeli) → reset i fetch
    '$route.params.slug'() {
      this.resetProductState();
      this.fetchProduct().then(this.fetchOtherProducts);
    }
  },
mounted() {
  this.isMobile = window.innerWidth <= 768;

  this.onScroll = this.throttle(() => {
    const y = window.scrollY || window.pageYOffset;
    const threshold = this.isMobile ? 300 : 600;
    const next = y > threshold;
    if (next !== this.showStickyCart) this.showStickyCart = next;

    // 1) pokaż opis po lekkim scrollu
    if (!this.descVisible && y > 200) {
      this.descVisible = true;
    }

    // 2) dociągnij polecane produkty trochę niżej
    if (!this.otherLoaded && y > 400) {
      this.otherLoaded = true;
      this.fetchOtherProducts();
    }
  }, 150);

  window.addEventListener("scroll", this.onScroll, { passive: true });

  this.prefetchIPCountry()
    .finally(() => this.fetchProduct());
},

beforeUnmount() {
  if (this.onScroll) {
    window.removeEventListener("scroll", this.onScroll);
  }
},


  methods: {
    throttle(fn, wait) {
    let last = 0;
    return (...args) => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn(...args);
      }
    };
    },
    // --- KLUCZOWE: reset stanu przy zmianie produktu ---
    resetProductState() {
      this.product = {};
      this.thumbnails = [];
      this.mainImage = null;

      this.variantGroups = {};
      this.selectedVariant = null;
      this.selectedVariantId = "";

      this.availableCountries = [];
      this.selectedCountry = '';
      this.selectedShippingPrice = 0;
      this.selectedDeliveryTime = 0;

      this.error = null;
      this.loading = true;
    },

    /* IP → ISO (jednorazowo) */
    async prefetchIPCountry() {
      try {
        const qs = new URLSearchParams(window.location.search).get('ship_to');
        if (qs) return;
        if (localStorage.getItem('ship_to_iso')) return;

        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
        clearTimeout(t);
        if (!res.ok) return;

        const data = await res.json();
        const iso = (data && data.country_code) ? String(data.country_code).toUpperCase() : '';
        if (iso) localStorage.setItem('ship_to_iso', iso);
      } catch (e) {
        // eslint-disable-next-line no-unused-vars
        const _ignored = e;
      }
    },

    async fetchProduct() {
      // czyść stan, żeby nie „przeciekały” stare ceny/zdjęcia
      this.resetProductState();

      const slug = this.$route.params.slug;
      try {
        const res = await fetch(`https://birdstool.pl/api/v1/products/${slug}/`, {
          headers: { 'Accept-Language': currentLang.value }
        });
        if (!res.ok) throw new Error("Błąd API");
        const data = await res.json();

        this.product = data;
        this.thumbnails = [
          data.get_image,
          data.get_thumbnail,
          ...(data.images?.map(img => img.image_url) || [])
        ].filter(Boolean);
        this.mainImage = this.thumbnails[0] || data.get_image;

        const grouped = {};
        (data.variants || []).forEach((v) => {
          const group = v.type?.name || "Wariant";
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push(v);
        });
        if (Object.keys(grouped).length === 0 && (data.variants || []).length) {
          grouped["Wariant"] = data.variants;
        }
        this.variantGroups = grouped;

        // ustaw pierwszy wariant (żeby od razu były dobre ceny)
        if (this.allVariants.length) {
          this.selectVariant(this.allVariants[0]);
        }

        this.loading = false;
      } catch (err) {
        this.error = err.message;
        this.loading = false;
      }
    },

    async fetchOtherProducts() {
      try {
        const res = await fetch('https://birdstool.pl/api/v1/latest-products/', {
          headers: { 'Accept-Language': currentLang.value }
        });
        const list = await res.json();
        this.otherProducts = (list || [])
          .filter(p => p.id !== this.product.id)
          .sort((a,b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
      } catch (e) {
        // eslint-disable-next-line no-unused-vars
        const _ignored = e;
        this.otherProducts = [];
      }
    },

    scrollRec(dir) {
      const row = this.$refs.recRow;
      if (!row) return;
      const card = row.querySelector('.rec-item');
      const step = (card?.offsetWidth || 240) * 1.2;
      row.scrollBy({ left: dir * step, behavior: 'smooth' });
    },

    changeMainImage(img) { this.mainImage = img; },

    /* DETEKCJA KRAJU */
    getUserCountryISO() {
      const qs = new URLSearchParams(window.location.search).get('ship_to');
      if (qs) return qs.toUpperCase();
      const stored = localStorage.getItem('ship_to_iso');
      if (stored) return stored.toUpperCase();
      const m = (navigator.language || '').match(/-([A-Za-z]{2})$/);
      return m ? m[1].toUpperCase() : '';
    },
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      };
      return m[String(code).toUpperCase()] || 'en';
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso);
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' });
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase();
      } catch {
        return String(iso).toUpperCase();
      }
    },
    countryLabel(iso) {
      const code = String(iso).toUpperCase();
      return this.countryEndonym(code);
    },
    saveUserCountryISO(iso) {
      if (iso) localStorage.setItem('ship_to_iso', String(iso).toUpperCase());
    },

    /* WARIANT + KRAJE WYSYŁKI */
    selectVariant(variant) {
      this.selectedVariant = variant;
      this.selectedVariantId = String(variant?.id ?? "");

      this.availableCountries = (variant.shipping_options || []).map(opt => {
        const code = String(opt.country || '').toUpperCase();
        const label = /^[A-Z]{2}$/.test(code) ? this.countryLabel(code) : (opt.country_name || code);
        return {
          code,
          name: label,
          PLN: Number(opt.price_pln ?? 0),
          EUR: Number(opt.price_eur ?? 0),
          USD: Number(opt.price_usd ?? 0),
          CZK: Number(opt.price_czk ?? 0),
          HUF: Number(opt.price_huf ?? 0),
          BGN: Number(opt.price_bgn ?? 0),
          RON: Number(opt.price_ron ?? 0),
          DKK: Number(opt.price_dkk ?? 0),
          SEK: Number(opt.price_sek ?? 0),
          delivery_time: Number(opt.delivery_time ?? 0)
        };
      });

      const preferred = (this.getUserCountryISO() || '').toUpperCase();
      const codes = this.availableCountries.map(c => c.code);
      this.selectedCountry = codes.includes(preferred) ? preferred : (codes[0] || '');

      this.updateShippingPrice();
      // GA4 + Pixel: view_item
      const priceStr = this.getPriceForCurrency(variant) || '0';
      const price = Number(String(priceStr).replace(/\s/g,'').replace(',','.')) || 0;
      track.viewItem({ product: this.product, variant, price, currency: this.currentCurrency });
    },

    updateShippingPrice() {
      const sel = (this.selectedCountry || '').toUpperCase();
      const option = this.availableCountries.find(o => String(o.code).toUpperCase() === sel);
      if (!option) {
        this.selectedShippingPrice = 0;
        this.selectedDeliveryTime = 0;
        return;
      }
      this.selectedDeliveryTime = option.delivery_time || 0;
      const val = Number(option?.[this.currentCurrency] ?? 0);
      this.selectedShippingPrice = Number.isFinite(val) ? val : 0;
    },

    getPriceForCurrency(variant) {
      if (!variant) return '';
      const found = (variant.prices || []).find(
        p => p.currency?.code === this.currentCurrency
      );
      if (!found?.amount) return '';
      return Number(found.amount)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    getListPriceForCurrency(product) {
      const map = {
        PLN: 'price_pln', EUR: 'price_eur', USD: 'price_usd',
        CZK: 'price_czk', HUF: 'price_huf', BGN: 'price_bgn',
        RON: 'price_ron', DKK: 'price_dkk', SEK: 'price_sek'
      };
      const key = map[this.currentCurrency] || 'price_pln';
      const n = Number(product?.[key]);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    },

    getCurrencySymbol() {
      const symbols = {
        PLN: 'zł', USD: '$', EUR: '€', CZK: 'Kč', HUF: 'Ft',
        BGN: 'лв', RON: 'lei', DKK: 'kr.', SEK: 'kr'
      };
      return symbols[this.currentCurrency] || this.currentCurrency;
    },

    addToCart() {
      if (!this.selectedVariant) {
        return alert(this.translations.product.no_variant || 'Nie wybrano wariantu!');
      }
      const variant = this.selectedVariant;
      const price = this.getPriceForCurrency(variant);

      let cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const idx = cart.findIndex(
        item =>
          item.slug === this.product.slug &&
          item.id === this.product.id &&
          item.variantKey === String(variant.id)
      );

      if (idx > -1) {
        cart[idx].quantity++;
      } else {
        cart.push({
          id: this.product.id,
          slug: this.product.slug,
          name: this.product.name,
          price: Number(String(price).replace(/\s/g, '').replace(',', '.')) || 0,
          currency: this.currentCurrency,
          quantity: 1,
          variantKey: String(variant.id),
          variantId: Number(variant.id),
          variants: [{
            id: variant.id,
            value: variant.type?.name,
            type: variant.type?.name
          }]
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      // GA4 + Pixel: add_to_cart
      track.addToCart({
       product: this.product,
       variant,
       price: Number(String(price).replace(/\s/g,'').replace(',','.')) || 0,
       quantity: 1,
       currency: this.currentCurrency
     });
      this.toastMessage = `${this.product.name} ${this.translations.product.added_to_cart_suffix || 'został dodany pomyślnie.'}`;
      this.showToast = true;
      setTimeout(() => this.showToast = false, 3000);
      eventBus.emit('cartUpdated');
    },

    nextImage() {
      const index = this.thumbnails.indexOf(this.mainImage);
      const nextIndex = (index + 1) % this.thumbnails.length;
      this.mainImage = this.thumbnails[nextIndex];
    },
    prevImage() {
      const index = this.thumbnails.indexOf(this.mainImage);
      const prevIndex = (index - 1 + this.thumbnails.length) % this.thumbnails.length;
      this.mainImage = this.thumbnails[prevIndex];
    },
    handleTouchStart(e) { this.touchStartX = e.changedTouches[0].screenX; },
    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const diff = this.touchStartX - touchEndX;
      if (diff > 50) this.nextImage();
      else if (diff < -50) this.prevImage();
    },

    closeDrawer() { this.isDrawerOpen = false; },
  }
};
</script>

<style scoped>
/* NAGŁÓWEK NAD RAMKĄ */
.picker-heading{
  font-weight:700; font-size:.95rem;
  margin:.4rem 0 .5rem; opacity:.85;
}

/* RAMKA: lewo TYPE.NAME, prawo CENA + caret */
.variant-picker{
  display:flex; align-items:center; gap:1rem;
  width:100%; padding:.9rem 1rem;
  border:1px solid #e5e7eb; border-radius:12px;
  background:#fff; cursor:pointer; margin:.25rem 0 1rem;
}
.picker-value{ font-weight:600; }
.picker-right{ margin-left:auto; display:flex; align-items:center; gap:.5rem; }
.picker-price{ font-weight:800; }
.picker-caret{ width:18px; height:18px; color:#6b7280; }

/* BACKDROP */
.variant-backdrop{ position:fixed; inset:0; background:rgba(0,0,0,.38); backdrop-filter:blur(2px); z-index:1100; }

/* BOTTOM-SHEET */
.variant-sheet{
  position:fixed; left:50%; bottom:0; transform:translate(-50%, 100%);
  width:100%; max-width:560px; height:min(85vh, 720px);
  background:#fff; border-top-left-radius:16px; border-top-right-radius:16px;
  box-shadow:0 -16px 32px rgba(0,0,0,.18);
  transition:transform .28s ease; z-index:1110;
  display:grid; grid-template-rows:auto 1fr auto; border:1px solid #eef1f6; border-bottom:none;
}
.variant-sheet.open{ transform:translate(-50%, 0); }
.variant-sheet-header{
  display:flex; align-items:center; justify-content:space-between;
  padding:1rem 1.25rem; border-bottom:1px solid #f1f5f9;
}
.sheet-close{ border:none; background:transparent; cursor:pointer; font-size:1.25rem; line-height:1; }
.variant-sheet-body{ overflow:auto; padding:1rem 1.25rem 0; }
.variant-sheet-footer{ padding:1rem 1.25rem 1.25rem; border-top:1px solid #f1f5f9; }

/* Lista wariantów */
.sizes-column{ display:flex; flex-direction:column; gap:.5rem; }
.size-row{
  display:flex; justify-content:space-between; align-items:center; width:100%;
  padding:.75rem .9rem; border-radius:12px; background:#fff;
  border:1px solid #e5e7eb; cursor:pointer;
}
.size-row:hover{ border-color:#c7cad1; }
.size-row.active{ border-color:#111827; box-shadow:0 0 0 2px #111827 inset; }
.size-name{ font-weight:600; }
.size-price{ opacity:.95; font-weight:800; }

/* POLECANE – karuzela */
.rec-wrap{ margin:1.5rem 0; }
.rec-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:.6rem; }
.rec-title{ font-weight:800; font-size:1.05rem; }
.rec-controls{ display:flex; gap:.35rem; }
.rec-btn{
  width:34px; height:34px; border-radius:50%;
  background:#fff; border:1px solid #e5e7eb; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  font-size:18px; line-height:1;
}
.rec-row{
  display:flex; gap:1rem; overflow-x:auto; padding-bottom:.5rem;
  scroll-behavior:smooth; -webkit-overflow-scrolling:touch;
  overscroll-behavior-x:contain; scroll-snap-type:x proximity;
}
.rec-item{ flex:0 0 auto; width:220px; scroll-snap-align:start; }
.rec-row::-webkit-scrollbar{ height:0; }

.main-image-wrap { aspect-ratio: 1 / 1; }
.main-image { width: 100%; height: 100%; object-fit: contain; display: block; }
.thumb { width: 96px; height: 96px; object-fit: cover; }
.thumbs-row--hidden{
  display: none !important;
}

</style>
